/**
 * ResumeInsights — what the parser pulled out of the resume: years of
 * experience, summary, employment timeline, certifications and skills.
 * Rendered only when `ai_fields.resume` exists.
 */
import { Award } from 'lucide-react';
import type { ResumeExtraction } from '@/features/pipeline/types';
import { EmploymentHistory } from './EmploymentHistory';
import { yearsLabel } from './resume-format';

function Chips({ items, strong = false, label }: { items: string[]; strong?: boolean; label: string }) {
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={label}>
      {items.map((s) => (
        <li
          key={s}
          className={`rounded-md px-2 py-0.5 text-[12px] ${
            strong ? 'bg-navy/10 font-medium text-navy' : 'border border-gray-200 bg-white text-gray-800'
          }`}
        >
          {strong && <Award size={11} strokeWidth={2.2} className="-mt-0.5 mr-1 inline" aria-hidden />}
          {s}
        </li>
      ))}
    </ul>
  );
}

export function ResumeInsights({ data }: { data: ResumeExtraction }) {
  const years = yearsLabel(data.years_experience);
  return (
    <div className="space-y-4">
      <section>
        <div className="mb-1.5 flex items-baseline justify-between gap-3">
          <h3 className="text-[13px] font-semibold text-gray-900">Employment history</h3>
          {years && (
            <span className="text-[12px] text-muted">
              <span className="font-semibold tabular-nums text-gray-900">{years}</span> experience
            </span>
          )}
        </div>
        {data.summary && <p className="mb-3 text-[13px] leading-5 text-gray-800">{data.summary}</p>}
        <EmploymentHistory entries={data.employment_history} />
      </section>

      {data.certifications.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-[13px] font-semibold text-gray-900">Certifications</h3>
          <Chips items={data.certifications} strong label="Certifications" />
        </section>
      )}

      {data.skills.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-[13px] font-semibold text-gray-900">Skills</h3>
          <Chips items={data.skills} label="Skills" />
        </section>
      )}
    </div>
  );
}
